import { PageHeader } from '@ant-design/pro-components';
import * as S from '@unionfab/ufc-shop-commons';
import { Badge, Space, Tabs } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import cn from 'classnames';

import LeftCircleIconII from '@/assets/svgs/LeftCircleII.svg';
import { useOrderDetailsStore } from '@/stores';
import { OrderCheckoutButton } from '../../components/OrderCheckoutButton';
import { OrderQualityInspectionButton } from '../../components/OrderQualityInspectionButton';
import { OrderInvoiceBtn } from '../../components/OrdersTable/OrderInvoiceBtn';
import { OrderDetailMain } from './OrderInfoTable/OrderDetailMain';
import { MessagePage } from './OrderInfoTable/OrderMessage';
import CustomerMessagesIcon from './OrderInfoTable/OrderDetailIcon/CustomerMessagesIcon';
import DeliveryDetailsIcon from './OrderInfoTable/OrderDetailIcon/DeliveryDetailsIcon';
import {
  queryOrderNewMessageCount,
  useAsyncEffect,
} from '@unionfab/ufc-shop-commons';

import styles from './OrderDetails.module.less';

/**
 * 订单详情页面组件入口
 * 包含订单详情和消息列表
 */
export const OrderDetail = ({
  title,
  isQuote,
}: {
  title: string;
  isQuote: boolean;
}) => {
  const navigate = useNavigate();
  const { orderCode } = useParams<{ orderCode: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab');

  // 状态管理
  const [activeKey, setActiveKey] = useState(
    tabFromUrl === 'messages' ? 'messages' : 'details',
  );
  const [userId, setUserId] = useState('');
  const [saleId, setSaleId] = useState('');
  const [totalMessageCount, setTotalMessageCount] = useState(0);

  // 从store获取订单数据
  const { storeShopOrder, loadShopOrder, loadOrder } = useOrderDetailsStore(
    d => ({
      storeShopOrder: d.shopOrder,
      loadShopOrder: d.loadShopOrder,
      loadOrder: d.loadOrder,
    }),
  );

  // 当前显示的订单
  const shopOrder =
    storeShopOrder?.code === orderCode ? storeShopOrder : undefined;
  const shopOrderStatus = shopOrder?.status;

  const { materials, loadMaterials } = S.useInquiryMaterialStore(m => ({
    materials: m.materials,
    loadMaterials: m.loadMaterials,
  }));

  const store = S.getUseShopQuoteStoreByCode(orderCode);

  const { shopOrderData, shopOrderDraft } = store(s => ({
    shopOrderData: s.shopOrder,
    shopOrderDraft: s.shopOrderDraft,
  }));

  const { groupId, loadQuotationGroupId } = S.useGlobalStore(g => ({
    groupId: g.quotationGroupId,
    loadQuotationGroupId: g.loadQuotationGroupId,
  }));

  useAsyncEffect(async () => {
    if (orderCode) {
      try {
        await loadQuotationGroupId();
        await loadOrder(orderCode);
      } catch (e) {
        console.log('>>>OrderDetails>>>err', e);
      }
    }
  }, [orderCode]);

  useEffect(() => {
    if (groupId) {
      loadMaterials(groupId);
    }
  }, [groupId]);

  const expandedSkus = useMemo(() => {
    if (!shopOrderData || !shopOrderData.items || materials?.length === 0)
      return [];
    return S.findExpandedSku(
      shopOrderData.items.map(item => ({
        materialId: item?.materialSkuVO?.materialId,
        skuId: item?.skuId,
      })),
      materials || [],
    );
  }, [shopOrderData, materials]);

  useEffect(() => {
    // 需要更新sku
    if (expandedSkus.length > 0) {
      S.ShopOrderQuoteStoreModifiers.updateOrderDraft(orderCode, o => {
        for (const item of o.items) {
          const expandedSku = expandedSkus.find(s => s.oldSkuId === item.skuId);
          if (expandedSku) {
            item.skuId = expandedSku.newSkuId;
            item.materialSkuVO = materials.find(
              m => m.id === expandedSku.materialId,
            )?.spuList[0];
          }
        }
      });

      store.setState({ shopOrder: shopOrderDraft });

      S.ShopOrderQuoteStoreModifiers.debouncedOnOrderUpdateSideEffect(
        orderCode,
        () => loadShopOrder(orderCode),
      );
    }
  }, [expandedSkus]);

  /**
   * 获取订单基本信息和相关人员信息
   */
  const getOrderInfo = async () => {
    if (!orderCode) return;

    try {
      const { data } =
        (await S.getUfcShopComposedApi().umiRequestCompatibleApi.getUmi(
          `/customer/order/${orderCode}`,
          {
            params: {
              tenantId: S.getTenantIdFromGConfig(),
              withCustomerShipOption: true,
              withCustomer: true,
              withQuotationUserInfo: true,
              withDispatchUserInfo: true,
            },
          },
        )) || { data: null };

      if (data?.customer) {
        setUserId(data.customer.representativeId);
      }

      if (data?.dispatchUserInfo && data?.dispatchUser) {
        const adminId = data.dispatchUser.adminId;
        const sale = data.dispatchUserInfo[adminId];
        // const sale: any = Object.values(data.dispatchUserInfo)[0];
        if (sale?.person) {
          setSaleId(sale.person.personId);
        }
      }
    } catch (error) {
      console.error('Failed to fetch order info:', error);
    }
  };

  /**
   * 获取订单消息数量
   */
  const fetchNotifications = async () => {
    if (!orderCode) return;

    try {
      const response = await queryOrderNewMessageCount([orderCode]);
      if (response.code === 'OK') {
        const messageCount = response.data?.[orderCode] || 0;
        setTotalMessageCount(messageCount);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  /**
   * 处理标签页切换
   */
  const handleTabChange = (key: string) => {
    setActiveKey(key);
    navigate(`${location.pathname}?tab=${key}`, { replace: true });
  };

  /**
   * 刷新消息计数
   */
  const refreshMessageCount = (count: number) => {
    setTotalMessageCount(count);
  };

  // 监听URL中的tab参数变化
  useEffect(() => {
    const tab = queryParams.get('tab');
    if (tab === 'messages' || tab === 'details') {
      setActiveKey(tab);
    }
  }, [location.search]);

  // 初始化数据
  useEffect(() => {
    if (orderCode) {
      loadShopOrder(orderCode);
      getOrderInfo();
      fetchNotifications();
    }
  }, [orderCode]);

  // 如果没有订单编号，显示空内容
  if (!orderCode) {
    return null;
  }

  // 页头组件 - 包含返回按钮和操作按钮
  const OrderPageHeader = useMemo(() => {
    const orderActionButtons = (
      <Space>
        <OrderInvoiceBtn order={shopOrder} buttonType="primary" />
        <OrderQualityInspectionButton shopOrder={shopOrder} />
        <OrderCheckoutButton
          code={orderCode}
          status={shopOrderStatus}
          hasAddress={S.get(shopOrder, o => o.delivery?.items, []).length > 0}
          style={{ height: '32px', display: 'flex' }}
        />
      </Space>
    );

    return (
      <PageHeader
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#000000',
              }}
            >{`Order#${orderCode}`}</span>
            <div style={{ position: 'absolute', right: 0 }}>
              {orderActionButtons}
            </div>
          </div>
        }
        className={styles.pageHeader}
        onBack={() => navigate(-1)}
        backIcon={<LeftCircleIconII />}
      />
    );
  }, [orderCode, shopOrderStatus, shopOrder]);

  // 标签页配置
  const tabItems = [
    {
      key: 'details',
      label: (
        <span className={styles.tabLabelContainer}>
          <DeliveryDetailsIcon isActive={activeKey === 'details'} />
          <span
            className={cn(
              styles.tabLabel,
              activeKey === 'details' && styles.isActiveLabel,
            )}
          >
            Details
          </span>
        </span>
      ),
    },
    {
      key: 'messages',
      label: (
        <Badge count={totalMessageCount} color="#F59E0B" offset={[15, -3]}>
          <span className={styles.tabLabelContainer}>
            <CustomerMessagesIcon isActive={activeKey === 'messages'} />
            <span
              className={cn(
                styles.tabLabel,
                activeKey === 'messages' && styles.isActiveLabel,
              )}
            >
              Messages
            </span>
          </span>
        </Badge>
      ),
    },
  ];

  return (
    <div className={styles.detailContainer}>
      {OrderPageHeader}
      <Tabs activeKey={activeKey} onChange={handleTabChange} items={tabItems} />

      {activeKey === 'details' && (
        <div className={styles.detailContent}>
          <OrderDetailMain title={title} isQuote={isQuote} />
        </div>
      )}

      {activeKey === 'messages' && (
        <div className={styles.detailContent} style={{ marginTop: '0' }}>
          <MessagePage
            key={orderCode}
            code={orderCode}
            userId={userId}
            saleId={saleId}
            onMessagesChange={refreshMessageCount}
          />
        </div>
      )}
    </div>
  );
};
